import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { WEB_COMPONENT_GRID_VIEW } from '../../../../webcomponents.constants';
import { ColumnKeys, FILTERS, LiveSearchMetadataGridViewData, PREDEFINED_FILTERS_KEYS } from '../types';
import { defaultPageSizeOptions, ViewConfiguration } from '../../../../grid/gridView/types';
import { TemplateState } from '../../../../types/common/types';
import { LiveSearchMetadataGridDataProvider } from '../dataProvider/LiveSearchMetadataGridDataProvider';
import {
  availableRowsRenderer,
  errorMessageRowsRenderer,
  metadataStatusRenderer,
  queryExecutionTimeRenderer,
  sentRowsRenderer,
} from './columnRenderers';
import { generateEndpointLink, isQueryExecutionFinished } from '../../utils';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzTooltipWebComponent } from '../../../../ui/gzTooltip/index';

export default class LiveSearchMetadataGridView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $gridContainer: HTMLElement;
    $assignTagsButton: GzButtonWebComponent;
    $grid: GridViewWebComponent<any, any>;
    $notStartedContainer: HTMLElement;
    $notStartedMessage: GzTitleWebComponent;
    $notStartedAdditionalText: GzTextWebComponent;
    $noResultsContainer: HTMLElement;
    $noResultsMessage: GzTitleWebComponent;
    $inProgressContainer: HTMLElement;
    $inProgressMessage: GzTitleWebComponent;
    $downloadMetadataTooltip: GzTooltipWebComponent;
  };

  public getTranslations(): void {
    this.translations = this.stringsTranslator.getMetadataGridStrings();
  }

  public setTranslatedStrings(): void {
    this.dom.$assignTagsButton.innerText = this.translations.assignTagsLabel;
    this.dom.$notStartedMessage.innerText = this.translations.notStartedMessage;
    this.dom.$notStartedAdditionalText.innerText = this.translations.notStartedAdditionalText;
    this.dom.$noResultsMessage.innerText = this.translations.noResultsMessage;
    this.dom.$inProgressMessage.innerText = this.translations.inProgressMessage;
    this.dom.$downloadMetadataTooltip.tooltipText = this.translations.downloadMetadata;
  }

  public getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridContainer = this.shadow.querySelector('.grid');
    this.dom.$assignTagsButton = this.shadow.querySelector('#assign-tags-button');
    this.dom.$downloadMetadataTooltip = this.shadow.querySelector('#download_metadata');
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$notStartedContainer = this.shadow.querySelector('#not-started');
    this.dom.$notStartedMessage = this.shadow.querySelector('#not-started-message');
    this.dom.$notStartedAdditionalText = this.shadow.querySelector('#not-started-additional-text');
    this.dom.$noResultsContainer = this.shadow.querySelector('#no-results');
    this.dom.$noResultsMessage = this.shadow.querySelector('#no-results-message');
    this.dom.$inProgressContainer = this.shadow.querySelector('#in-progress');
    this.dom.$inProgressMessage = this.shadow.querySelector('#in-progress-message');

    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start();
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public buildSuccessView(data: LiveSearchMetadataGridViewData): void {
    this.resetTemplateState(this.dom.$wrapper);

    this.dom.$grid.dataProvider = new LiveSearchMetadataGridDataProvider(this.dependencies, data.queryExecution);

    const view: ViewConfiguration<any, any> = data.views[0];
    this.dom.$grid.viewId = view.id;
    this.dom.$grid.filters = view.filters;
    this.dom.$grid.dataProvider.setFilters({
      filters: view.filters,
      viewId: view.id,
    });
    this.dom.$grid.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$grid.columns = view.displayedColumns;
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.ENDPOINT]: generateEndpointLink.bind(this),
      [ColumnKeys.STATUS]: metadataStatusRenderer.bind(this),
      [ColumnKeys.SENT_ROWS]: sentRowsRenderer.bind(this),
      [ColumnKeys.AVAILABLE_ROWS]: availableRowsRenderer.bind(this),
      [ColumnKeys.QUERY_EXECUTION_TIME]: queryExecutionTimeRenderer.bind(this),
      [ColumnKeys.ERROR_MESSAGE]: errorMessageRowsRenderer.bind(this),
    };

    this.dom.$grid.pageSize = view.pageSize;
    this.dom.$grid.isCompactViewChecked = view.compactView;
    this.dom.$grid.allColumnDetails = data.allColumns;
    this.dom.$grid.defaultViewProps = data.defaultViewProps;
    this.dom.$grid.hideFiltersToggle = true;
    this.dom.$grid.disableGridSettings = true;
    this.dom.$grid.hideHeaderActions = false;
    this.dom.$grid.hideFooter = false;
    this.dom.$grid.hasSelectableRows = true;
    this.dom.$grid.hasSelectionExtraOptions = true;
    this.dom.$grid.hasPagination = true;
    this.dom.$grid.pageSizeOptions = defaultPageSizeOptions;
    this.dom.$grid.propertiesSet = true;

    if (!data.queryExecution) {
      this.showNotStartedPanel();
    } else if (!isQueryExecutionFinished(data.queryExecution)) {
      //doesn't have results
      this.showInProgressPanel();
    } else if (!data.queryExecution.hasExpiredResultsMetadata && data.queryExecution.endpointsStatistics?.totalCount) {
      //has results and are not expired
      this.showGrid();
    } else {
      this.showNoResultsPanel();
    }
  }

  public buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public setAssignTagsButtonState(disabled: boolean): void {
    this.dom.$assignTagsButton.disabled = disabled;
  }

  public refreshGrid(): void {
    this.dom.$grid.controller?.render();
  }

  private showNotStartedPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.removeElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
  }

  private showNoResultsPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.removeElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
  }

  private showInProgressPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.removeElementClass(this.dom.$inProgressContainer, 'hidden');
  }

  private showGrid(): void {
    this.removeElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
  }
}
